import React, { useState, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import { FolderPlus, UserPlus } from "lucide-react";
import { listProjectUsers } from "../services/userService";

const baseConfig = {
  iconBg: "var(--primaryTint)",
  iconColor: "var(--primaryColor)",
  btnBg: "var(--primaryColor)",
};

const typeConfig = {
  project: {
    ...baseConfig,
    Icon: FolderPlus,
    title: "Create Project",
    nameLabel: "Project Name",
    namePlaceholder: "Enter project name",
  },
  manager: {
    ...baseConfig,
    Icon: UserPlus,
    title: "Add Manager",
    nameLabel: "Manager Name",
    namePlaceholder: "Enter manager name",
  },
};

const EMPTY_CREATE = { firstName: "", lastName: "", email: "", role: "" };
const EMPTY_ASSIGN = { userPid: "" };

const CreateEntityModal = ({ show, onClose, onConfirm, type = "project", error = "", loading = false, projectPid = "" }) => {
  const [form, setForm]             = useState({ name: "", email: "", role: "admin" });
  const [mode, setMode]             = useState("create");
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [assignForm, setAssignForm] = useState(EMPTY_ASSIGN);

  const [nameQuery, setNameQuery]         = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const suggestRef  = useRef(null);
  const debounceRef = useRef(null);

  const cfg = typeConfig[type] || typeConfig.project;
  const { Icon } = cfg;

  useEffect(() => {
    if (show) {
      resetForm();
      setMode("create");
      setCreateForm(EMPTY_CREATE);
      setAssignForm(EMPTY_ASSIGN);
      setNameQuery("");
      setShowSuggestions(false);
      setSearchResults([]);
    }
  }, [show]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (suggestRef.current && !suggestRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleNameInput = (val) => {
    setNameQuery(val);
    if (!val.trim()) {
      setAssignForm((p) => ({ ...p, userPid: "" }));
      setSearchResults([]);
      setShowSuggestions(false);
      clearTimeout(debounceRef.current);
      return;
    }
    setShowSuggestions(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchLoading(true);
      listProjectUsers({ projectPid, search: val.trim(), page: 1, limit: 5 })
        .then((res) => {
          const data = res.data?.response ?? res.data ?? {};
          const items = Array.isArray(data) ? data : (data.items ?? data.rows ?? []);
          setSearchResults(items);
        })
        .catch(() => setSearchResults([]))
        .finally(() => setSearchLoading(false));
    }, 300);
  };

  const selectSuggestion = (user) => {
    const pid      = user.user_pid ?? user.userPid ?? "";
    const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ");
    setNameQuery(fullName);
    setAssignForm((p) => ({ ...p, userPid: pid }));
    setShowSuggestions(false);
  };

  const handleChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = () => {
    if (type === "manager") {
      if (mode === "create") {
        if (!createForm.firstName.trim() || !createForm.lastName.trim() || !createForm.email.trim() || !createForm.role) return;
        onConfirm({ _mode: "create", ...createForm });
      } else {
        if (!assignForm.userPid.trim()) return;
        onConfirm({ _mode: "assign", ...assignForm });
      }
      return;
    }
    if (!form.name.trim()) return;
    onConfirm({ ...form });
  };

  const resetForm = () => setForm({ name: "", email: "", role: "admin" });

  const tabStyle = (active) => ({
    flex: 1,
    padding: "8px 0",
    fontSize: 13,
    fontWeight: 600,
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    transition: "all .15s",
    background: active ? "var(--primaryColor)" : "transparent",
    color: active ? "#fff" : "#64748b",
  });

  return (
    <Modal
      show={show}
      onHide={() => { resetForm(); onClose(); }}
      centered
      backdrop="static"
      size="md"
    >
      <Modal.Header closeButton className="cem-header border-0 pb-0">
        <div className="cem-header-inner">
          <div className="cem-icon" style={{ background: cfg.iconBg, color: cfg.iconColor }}>
            <Icon size={22} />
          </div>
          <Modal.Title className="cem-title">{cfg.title}</Modal.Title>
        </div>
      </Modal.Header>

      <Modal.Body className="cem-body">
        {type === "manager" ? (
          <>
            {/* Mode toggle */}
            <div style={{ display: "flex", background: "#f1f5f9", borderRadius: 10, padding: 4, marginBottom: 20 }}>
              <button style={tabStyle(mode === "create")} onClick={() => setMode("create")}>Create New</button>
              <button style={tabStyle(mode === "assign")} onClick={() => setMode("assign")}>Assign Existing</button>
            </div>

            {mode === "create" ? (
              <>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <div className="cem-field" style={{ flex: 1, minWidth: "120px" }}>
                    <label className="cem-label">First Name</label>
                    <input
                      type="text"
                      className="cem-input form-control"
                      placeholder="Enter first name"
                      value={createForm.firstName}
                      onChange={(e) => setCreateForm((p) => ({ ...p, firstName: e.target.value }))}
                    />
                  </div>
                  <div className="cem-field" style={{ flex: 1, minWidth: "120px" }}>
                    <label className="cem-label">Last Name</label>
                    <input
                      type="text"
                      className="cem-input form-control"
                      placeholder="Enter last name"
                      value={createForm.lastName}
                      onChange={(e) => setCreateForm((p) => ({ ...p, lastName: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="cem-field">
                  <label className="cem-label">Email</label>
                  <input
                    type="email"
                    className="cem-input form-control"
                    placeholder="Enter email address"
                    value={createForm.email}
                    onChange={(e) => setCreateForm((p) => ({ ...p, email: e.target.value }))}
                  />
                </div>
                <div className="cem-field mb-0">
                  <label className="cem-label">Role</label>
                  <select
                    className="cem-input form-select"
                    value={createForm.role}
                    onChange={(e) => setCreateForm((p) => ({ ...p, role: e.target.value }))}
                  >
                    <option value="" disabled>Select role</option>
                    <option value="project_admin">Project Admin</option>
                    <option value="manager">Manager</option>
                  </select>
                </div>
              </>
            ) : (
              <>
                {/* Name search — client-side filter */}
                <div className="cem-field" ref={suggestRef} style={{ position: "relative" }}>
                  <label className="cem-label">Search by Name</label>
                  <input
                    type="text"
                    className="cem-input form-control"
                    placeholder="Type a manager name…"
                    value={nameQuery}
                    autoComplete="off"
                    onChange={(e) => handleNameInput(e.target.value)}
                    onFocus={() => { if (nameQuery.trim()) setShowSuggestions(true); }}
                  />

                  {/* Dropdown */}
                  {showSuggestions && (
                    <div style={{
                      position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 9999,
                      background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
                      boxShadow: "0 8px 24px rgba(15,23,42,.12)", maxHeight: 230, overflowY: "auto",
                    }}>
                      {searchLoading ? (
                        <div style={{ padding: "14px 16px", textAlign: "center" }}>
                          <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>Searching…</p>
                        </div>
                      ) : searchResults.length === 0 ? (
                        <div style={{ padding: "14px 16px", textAlign: "center" }}>
                          <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>No users found for "{nameQuery}"</p>
                        </div>
                      ) : searchResults.map((user, idx) => {
                        const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ") || "—";
                        const initials = ((user.first_name?.[0] ?? "") + (user.last_name?.[0] ?? "")).toUpperCase() || "?";
                        const isLast   = idx === searchResults.length - 1;
                        return (
                          <div
                            key={user.user_pid ?? idx}
                            style={{
                              display: "flex", alignItems: "center", gap: 12,
                              padding: "10px 14px", cursor: "pointer",
                              borderBottom: isLast ? "none" : "1px solid #f1f5f9",
                              transition: "background .12s",
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
                            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                            onMouseDown={() => selectSuggestion(user)}
                          >
                            <div style={{
                              width: 34, height: 34, borderRadius: 8, background: "var(--primaryTint)", color: "var(--primaryColor)",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 12, fontWeight: 700, flexShrink: 0,
                            }}>
                              {initials}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fullName}</p>
                              <p style={{ margin: 0, fontSize: 11, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email ?? ""}</p>
                            </div>
                            {user.role && (
                              <span style={{
                                fontSize: 10, fontWeight: 600, textTransform: "capitalize",
                                background: "var(--primaryTint)", color: "var(--primaryColor)", borderRadius: 4, padding: "2px 7px",
                                flexShrink: 0,
                              }}>{user.role}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

              </>
            )}
          </>
        ) : (
          <div className="cem-field">
            <label className="cem-label">{cfg.nameLabel}</label>
            <input
              type="text"
              className="cem-input form-control"
              placeholder={cfg.namePlaceholder}
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />
          </div>
        )}
      </Modal.Body>

      {error && (
        <div style={{ margin: "0 20px 4px", padding: "10px 14px", background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 8, fontSize: 12, color: "#dc2626" }}>
          {error}
        </div>
      )}

      <Modal.Footer className="cem-footer border-0">
        <button className="cem-btn cem-btn-cancel" onClick={() => { resetForm(); onClose(); }} disabled={loading}>
          Cancel
        </button>
        <button
          className="cem-btn cem-btn-confirm"
          style={{ background: cfg.btnBg, opacity: loading ? 0.7 : 1 }}
          onClick={handleSubmit}
          disabled={loading}
        >
          <Icon size={15} />
          {loading ? "Saving…" : type === "manager" ? (mode === "create" ? "Create Manager" : "Assign Manager") : cfg.title}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateEntityModal;
